/**
 * WebSocket Slice для управления соединением
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface WSMessage {
  type: number;
  data: any;
  timestamp: number;
}

interface Order {
  id: string;
  pair: string;
  direction: 'call' | 'put';
  amount: number;
  duration: number;
  openPrice: number;
  timestamp: number;
  status: 'active' | 'closed' | 'cancelled';
  closePrice?: number;
  profit?: number;
  result?: string;
}

interface WebSocketState {
  isConnected: boolean;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  connection: WebSocket | null;
  lastMessage: any;
  messages: WSMessage[];
  prices: { [symbol: string]: number };
  balance: { demo: number; real: number };
  orders: Order[];
  error: string | null;
  reconnectAttempts: number;
}

const initialState: WebSocketState = {
  isConnected: false,
  connectionStatus: 'disconnected',
  connection: null,
  lastMessage: null,
  messages: [],
  prices: {},
  balance: { demo: 10000, real: 0 },
  orders: [],
  error: null,
  reconnectAttempts: 0,
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    setConnection: (state, action: PayloadAction<WebSocket | null>) => {
      state.connection = action.payload;
    },
    
    setConnectionStatus: (state, action: PayloadAction<WebSocketState['connectionStatus']>) => {
      state.connectionStatus = action.payload;
      state.isConnected = action.payload === 'connected';
    },
    
    addMessage: (state, action: PayloadAction<WSMessage>) => {
      state.messages.push(action.payload);
      state.lastMessage = action.payload;
      // Ограничиваем количество сохраненных сообщений
      if (state.messages.length > 100) {
        state.messages = state.messages.slice(-100);
      }
    },
    
    updatePrices: (state, action: PayloadAction<{ [symbol: string]: number }>) => {
      state.prices = { ...state.prices, ...action.payload };
    },
    
    updateBalance: (state, action: PayloadAction<{ demo: number; real: number }>) => {
      state.balance = action.payload;
    },
    
    addOrder: (state, action: PayloadAction<Order>) => {
      state.orders.push(action.payload);
    },
    
    updateOrder: (state, action: PayloadAction<Partial<Order> & { id: string }>) => {
      const index = state.orders.findIndex(order => order.id === action.payload.id);
      if (index !== -1) {
        const updatedOrder = { ...state.orders[index], ...action.payload };
        if (updatedOrder.status === 'closed') {
          state.orders.splice(index, 1);
        } else {
          state.orders[index] = updatedOrder;
        }
      }
    },
    
    setLastMessage: (state, action: PayloadAction<any>) => {
      state.lastMessage = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      if (action.payload) {
        state.connectionStatus = 'error';
      }
    },
    
    resetReconnectAttempts: (state) => {
      state.reconnectAttempts = 0;
    },
    
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setConnection,
  setConnectionStatus,
  addMessage,
  updatePrices,
  updateBalance,
  addOrder,
  updateOrder,
  setLastMessage,
  setError,
  resetReconnectAttempts,
  clearError,
} = websocketSlice.actions;

export default websocketSlice.reducer;